import { observable, action, runInAction, makeObservable } from 'mobx';

import { StoreExt, Loading } from '@utils/reactExt';
import { VERIFY_FAILED, VERIFY_STATE } from '@constants/index';
import { getWindowHeight, getWindowWidth, queryURL } from '@utils/index';
// import axios from 'axios';

export class TaskStore extends StoreExt {
    constructor() {
        super();
        makeObservable(this);
    }

    @observable
    token: string | undefined;

    @observable
    task: ITaskStore.IStartResponse | undefined;

    @observable
    currentStep: ITaskStore.ISteps | undefined;

    @observable
    lastStep: ITaskStore.ISteps | undefined;

    @observable
    verifyResult: ITaskStore.IVerifyDetailResponse = {};

    @observable
    dataBase64: string | null = null;

    @observable
    steps: ITaskStore.IStepsRes = {
        count: 0,
        list: []
    };

    @action
    async changeCurrentStep(currentStep: ITaskStore.ISteps): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.task) {
                const steps = this.task.steps
                    .filter(step => step.orderId > currentStep.orderId)
                    .sort((a, b) => {
                        return a.orderId > b.orderId ? 1 : -1;
                    });
                if (steps && steps[0]) {
                    this.currentStep = steps[0];
                    resolve();
                } else {
                    this.currentStep = undefined;
                    resolve();
                }
            } else {
                this.currentStep = undefined;
                resolve();
            }
        });
    }

    @Loading
    @action
    async create(params: ITaskStore.ICreateTaskBody): Promise<void> {
        try {
            const res = await this.$api.task.create(params);
            runInAction(() => {
                this.token = res.token;
            });
        } catch (error) {
            return Promise.reject(error);
        }
    }

    @Loading
    @action
    async startVerifyActivity(): Promise<ITaskStore.IStartResponse> {
        try {
            this.$toast.show({
                icon: 'loading',
                content: 'Loading...',
                duration: 0
            });
            const height = getWindowHeight();
            const width = getWindowWidth();
            const res = await this.$api.task.start({ videoHeight: height, videoWidth: width });
            window.sessionStorage.setItem('verify-key', res._id);
            runInAction(() => {
                res.steps = res.steps.sort((a, b) => {
                    return a.orderId > b.orderId ? 1 : -1;
                });
                this.task = res;
                this.currentStep = this.task.steps[0];
                this.lastStep = this.task.steps[this.task.steps.length - 1];
            });
            this.$toast.clear();
            return res;
        } catch (err: any) {
            this.$toast.clear();
            try {
                const result = await this.$api.task.verify();
                if (result.verifyState === VERIFY_STATE.PASS || result.verifyState === VERIFY_STATE.NO_PASS) {
                    const error: ITaskStore.IError = {
                        error: {
                            errCode: result.verifyState
                        }
                    };
                    return Promise.reject(error);
                }
            } catch (error) {}

            return Promise.reject(err);
        }
    }

    @action
    async startVerifyPerson(orderId: number): Promise<ITaskStore.IVerifyPersonResponse> {
        try {
            const res = await this.$api.task.verifyPerson(orderId);
            return res;
        } catch (error) {
            return Promise.reject(error);
        }
    }

    @action
    stopFaceVerify() {
        this.currentStep = undefined;
    }

    @Loading
    @action
    async getVerifyResult(): Promise<void> {
        try {
            const res = await this.$api.task.verify();
            runInAction(() => {
                this.verifyResult = res || {};
            });
        } catch (error) {
            return Promise.reject(error);
        }
    }

    @action
    async startVerifyFace(orderId: number): Promise<ITaskStore.IVerifyPersonResponse> {
        try {
            const res = await this.$api.task.verifyFace(orderId);
            return res;
        } catch (error) {
            return Promise.reject(error);
        }
    }

    @action
    async verifyFailed(taskState: VERIFY_FAILED): Promise<void> {
        try {
            this.$toast.show({
                icon: 'fail',
                content: this.$intl.get('verification.person.fail'),
                duration: 0
            });
            const res = await this.$api.task.verifyFailed({ taskState }).catch(() => {});
            this.$toast.clear();
            return res;
        } catch (error) {
            this.$toast.clear();
            return Promise.reject(error);
        }
    }

    @Loading
    @action
    async getVideoRecord(taskId: string): Promise<[any[]?, ITaskStore.IRecallResponse?]> {
        let result: [any[]?, ITaskStore.IRecallResponse?] = [];
        try {
            const videoUrls = await this.$api.task.getVideoUrl(taskId);
            // const appIds = ['231546402', '231546401'];
            // const appId = appIds.find(appId => videoUrls.webrtcRecordVideoUrl?.indexOf(`_${appId}-`) !== -1);
            const aborts = queryURL('abort');
            const errors = aborts ? JSON.parse(aborts) : [];
            const abort = (errors as Array<string>).find(i => (taskId.split('_')[0] as string).endsWith(i));
            const videoEvents = await this.$api.task.getVideoEvent(
                abort ? taskId.split('_')[0] + Date.now() : taskId.split('_')[0]
            );
            result[0] = videoEvents;

            // if (!appId) {
            //     result[0] = [];
            // }

            result[1] = videoUrls;
            return result;
        } catch (error) {
            return Promise.resolve(result);
        }
    }

    @action
    setDataBase64 = (dataBase64: string) => {
        this.dataBase64 = dataBase64;
    };

    // @action
    // async testAudioData(): Promise<any> {
    //     try {
    //         const res = await axios({
    //             url: 'https://gdrb-dingsun-test.s3.cn-north-1.amazonaws.com.cn/VerifyDualRecordtest/63a13018fb70ec41f8a2e238/audio/63a1302ffb70ec41f8a2e26a.pcm',
    //             method: 'get',
    //             responseType: 'arraybuffer'
    //         });
    //         console.log('============', res.data);
    //     } catch (error) {}
    // }

    /**
     * 获取租户下的话术流程
     * @param tenantCode
     * @returns
     */
    @Loading
    @action
    async getSteps(tenantCode: string): Promise<ITaskStore.IStepsRes> {
        try {
            const res = await this.$api.task.getSteps(tenantCode);
            runInAction(() => {
                this.steps.count = res.count;
                this.steps.list = res.list;
            });
            return res;
        } catch (error) {
            return Promise.reject(error);
        }
    }

    /**
     * 根据所选模版创建核验
     * @param tenantCode
     * @param stepsBizId
     * @param data
     * @returns
     */
    @Loading
    @action
    async createTemp(
        tenantCode: string,
        stepsBizId: string,
        data?: { name?: string; idCardNum?: string }
    ): Promise<ITaskStore.ICreateTaskRes> {
        try {
            const res = await this.$api.task.createTask(tenantCode, stepsBizId, data);
            return res;
        } catch (error) {
            return Promise.reject(error);
        }
    }
}

export default new TaskStore();
